import { asApiRefusal } from '../../utils/apiRefusal';

/**
 * Reading the service's refusals for the Model Catalog writes (task 1102).
 *
 * A 400 carries `details.validationErrors[{ field, message }]`, which the axios
 * interceptor lifts onto the rejected error as `validationErrors`. A 409 on a
 * create (duplicate provider key, duplicate Model id) may arrive without a
 * field, so the caller names the field it belongs to.
 */
export interface CatalogFormErrors {
  fields: Record<string, string>;
  /** A refusal no field claims, shown above the form actions. */
  general: string | null;
}

/**
 * The successor refusals (service task 1129): `400` when the successor is the
 * Model itself or would form a cycle, `422` when it is unknown or withdrawn.
 * Either may arrive without a field, so a field-less 400/422 whose message or
 * `details.field` names the successor is claimed by the Successor picker.
 */
function isSuccessorRefusal(refusal: ReturnType<typeof asApiRefusal>, message: string | null): boolean {
  if (refusal.status !== 400 && refusal.status !== 422) return false;
  const details = refusal.details;
  if (details && typeof details === 'object' && (details as Record<string, unknown>).field === 'successorModelId') return true;
  return !!message && /successor|cycle/i.test(message);
}

export function catalogFormErrors(
  error: unknown,
  conflictField?: string,
  options: { successorField?: string } = {},
): CatalogFormErrors {
  const refusal = asApiRefusal(error);
  const fields: Record<string, string> = {};
  let unclaimed: string | null = null;

  for (const entry of refusal.validationErrors ?? []) {
    const message = entry.message?.trim();
    if (!message) continue;
    if (entry.field && !fields[entry.field]) fields[entry.field] = message;
    else if (!entry.field && !unclaimed) unclaimed = message;
  }

  const top = typeof refusal.message === 'string' && refusal.message.trim() ? refusal.message.trim() : null;
  if (refusal.status === 409 && conflictField && Object.keys(fields).length === 0 && top) {
    fields[conflictField] = top;
    return { fields, general: unclaimed };
  }

  if (options.successorField && Object.keys(fields).length === 0 && isSuccessorRefusal(refusal, unclaimed ?? top)) {
    fields[options.successorField] = (unclaimed ?? top) as string;
    return { fields, general: null };
  }

  if (Object.keys(fields).length > 0) return { fields, general: unclaimed };
  return { fields, general: unclaimed ?? top ?? 'The service refused and gave no reason.' };
}

/**
 * The Model Configs a 409 disable names. Task 1101 specifies "a structured
 * list of blocking config names" without fixing the key, so this reads the
 * list from `details` under the names the service uses for in-use refusals
 * (`blocking`) and the plain ones (`configs`, `blockingConfigs`,
 * `modelConfigs`). Each entry may be a name or an object carrying `name`.
 */
export function blockingConfigNames(error: unknown): string[] {
  const details = asApiRefusal(error).details;
  if (!details || typeof details !== 'object') return [];
  const record = details as Record<string, unknown>;
  const list = [record.blocking, record.blockingConfigs, record.configs, record.modelConfigs].find(Array.isArray) as
    | unknown[]
    | undefined;
  if (!list) return [];
  return list
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object') {
        const item = entry as Record<string, unknown>;
        if (typeof item.name === 'string' && item.name) return item.name;
        // Model Config names are optional; an unnamed one is named by its Model.
        if (typeof item.model === 'string' && item.model) return `Unnamed (${item.model})`;
        return typeof item.id === 'string' ? item.id : null;
      }
      return null;
    })
    .filter((name): name is string => !!name);
}

/** True when the service refused the disable because the provider is the platform default. */
export function isDefaultProviderRefusal(error: unknown): boolean {
  const details = asApiRefusal(error).details;
  return !!details && typeof details === 'object' && (details as Record<string, unknown>).defaultProvider === true;
}
